import {
  ClipboardDocumentListIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getFamilles } from "./familleAPI";
import { getSocietes } from "./societeAPI";
import BlocIdentite from "./details/BlocIdentite";
import BlocLogement from "./details/BlocLogement";
import BlocObjectifs from "./details/BlocObjectifs";
import BlocPatrimoine from "./details/BlocPatrimoine";
import BlocRevenus from "./details/BlocRevenus";
import BlocDirigeants from "./details/BlocDirigeants";
import BlocCoordonnees from "./details/BlocCoordonnees";
import BlocFinancier from "./details/BlocFinancier";
import BlocDocuments from "./details/BlocDocuments";
import FamilleStatus from "./details/FamilleStatus";
import FamilleInteractions from "./details/FamilleInteractions";
import FamilleAudit from "./FamilleAudit";
import SocieteAudit from "./SocieteAudit";
import Loader from "../../../components/Loader";

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [activeTab, setActiveTab] = useState("Identité");

  useEffect(() => {
    const fetchData = async () => {
      const familles = await getFamilles();
      const societes = await getSocietes();
      const found = [...familles, ...societes].find((e) => e._id === id);
      setEntity(found);
    };
    fetchData();
  }, [id]);

  if (!entity) return <Loader />;

  const isSociete = entity.type === "Personne Morale";

  const tabsPhysique = ["Identité", "Logement", "Objectifs", "Patrimoine", "Revenus"];
  const tabsMorale = ["Identité", "Dirigeants", "Coordonnées", "Financier", "Documents"];
  const tabs = isSociete ? tabsMorale : tabsPhysique;

  const renderTabContent = () => {
    if (!isSociete) {
      switch (activeTab) {
        case "Logement":
          return <BlocLogement data={entity} />;
        case "Objectifs":
          return <BlocObjectifs data={entity} />;
        case "Patrimoine":
          return <BlocPatrimoine data={entity} />;
        case "Revenus":
          return <BlocRevenus data={entity} />;
        default:
          return <BlocIdentite data={entity} />;
      }
    } else {
      switch (activeTab) {
        case "Dirigeants":
          return <BlocDirigeants data={entity} />;
        case "Coordonnées":
          return <BlocCoordonnees data={entity} />;
        case "Financier":
          return <BlocFinancier data={entity} />;
        case "Documents":
          return <BlocDocuments data={entity} />;
        default:
          return <BlocIdentite data={entity} />;
      }
    }
  };

  const ActionButton = ({ icon, label, bgColor, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-sm text-white ${bgColor} hover:brightness-110 transition`}
    >
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/20">
        {icon}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );

  const initiales = isSociete
    ? entity.denomination?.slice(0, 2).toUpperCase()
    : `${entity.prenom?.charAt(0) ?? ""}${entity.nom?.charAt(0) ?? ""}`;

  const nomAffiche = isSociete ? entity.denomination : `${entity.nom} ${entity.prenom}`;

  return (
    <div className="flex flex-col h-[92vh] bg-gray-50 px-6 py-6">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl px-6 py-4 shadow flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-blue-600 text-white w-10 h-10 flex items-center justify-center rounded-full text-lg font-bold">
                {initiales}
              </div>
              <h1 className="text-xl font-semibold text-gray-800">{nomAffiche}</h1>
            </div>
            <nav className="border-b border-gray-200">
              <div className="-mb-px flex space-x-6">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`text-sm font-medium pb-2 border-b-2 transition-all ${
                      activeTab === tab
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </nav>
          </div>
          <div className="animate-fade-in">{renderTabContent()}</div>
        </div>

        <div className="space-y-6">
          <FamilleStatus famille={entity} />
          <FamilleInteractions famille={entity} />
        </div>
      </div>

      <div className="mt-6 border-t pt-6 flex justify-between items-center w-full">
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600 transition"
        >
          <ChevronLeftIcon className="w-5 h-5" />
          Retour
        </button>
        <div className="flex gap-3">
          <ActionButton
            icon={<ClipboardDocumentListIcon className="w-5 h-5 text-white" />}
            label="Audit patrimonial"
            bgColor="bg-blue-600"
            onClick={() => setShowAuditModal(true)}
          />
          <ActionButton
            icon={<Cog6ToothIcon className="w-5 h-5 text-white" />}
            label="Allocation"
            bgColor="bg-purple-600"
            onClick={() => navigate("/outils/allocation")}
          />
          <ActionButton
            icon={<BanknotesIcon className="w-5 h-5 text-white" />}
            label="Calcul des impôts"
            bgColor="bg-green-600"
            onClick={() => navigate("/outils/impots")}
          />
        </div>
      </div>

      {isSociete ? (
        <SocieteAudit open={showAuditModal} societe={entity} onClose={() => setShowAuditModal(false)} />
      ) : (
        <FamilleAudit open={showAuditModal} famille={entity} onClose={() => setShowAuditModal(false)} />
      )}
    </div>
  );
}
